import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, CheckCircle2, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Input, Textarea, FieldLabel } from "@/components/ui/Field";

const EMAIL = "chase.bradley.west@gmail.com";

const schema = z.object({
  name: z.string().min(2, "Enter your name"),
  email: z.string().email("Enter a valid email"),
  org: z.string().optional(),
  message: z.string().min(10, "A sentence or two on what you need"),
});
type Values = z.infer<typeof schema>;

const IDENTITY = [
  { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}` },
  { icon: MapPin, label: "Station", value: "Newport Beach, CA", href: undefined },
];

export function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [viaMailto, setViaMailto] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const onSubmit = async (v: Values) => {
    setSending(true);
    try {
      // Server-side delivery via the Cloudflare Worker (/api/contact).
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(v),
      });
      const ct = res.headers.get("content-type") || "";
      if (res.ok && ct.includes("application/json")) {
        const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
        if (data?.ok) {
          setViaMailto(false);
          setSent(true);
          return;
        }
      }
      throw new Error(String(res.status));
    } catch {
      // Fallback so a message is never lost: open the visitor's mail client.
      const subject = encodeURIComponent(`Project inquiry — ${v.name}`);
      const body = encodeURIComponent(
        `${v.message}\n\n— ${v.name}${v.org ? ` · ${v.org}` : ""}\n${v.email}`,
      );
      window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
      setViaMailto(true);
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          index="04"
          kicker="Contact"
          title={
            <>
              Have a hard problem? <span className="text-gradient-signal">Let's deploy.</span>
            </>
          }
          blurb="Embedded device, agent platform, air-gapped system, or something that doesn't fit a category yet — tell me the mission and I'll tell you how I'd take it on."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
          {/* identity card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="brackets glass flex flex-col rounded-[var(--radius-lg)] p-7"
          >
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-ink-400">
              Direct line
            </span>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink-50">
              Chase Bradley-West
            </h3>
            <p className="font-mono text-[0.72rem] uppercase tracking-[0.14em] text-signal-400">
              Forward Deployed Engineer
            </p>

            <div className="mt-7 flex flex-col divide-y divide-ink-800/80">
              {IDENTITY.map((row) => {
                const Icon = row.icon;
                const inner = (
                  <>
                    <span className="inline-flex size-9 items-center justify-center rounded-[var(--radius-md)] border border-ink-800 bg-ink-950/60 text-signal-500">
                      <Icon className="size-4" />
                    </span>
                    <span className="flex flex-col">
                      <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-ink-500">
                        {row.label}
                      </span>
                      <span className="text-sm font-medium text-ink-100">{row.value}</span>
                    </span>
                  </>
                );
                return row.href ? (
                  <a
                    key={row.label}
                    href={row.href}
                    target={row.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className="group flex items-center gap-3 py-3 transition-colors hover:text-signal-300"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={row.label} className="flex items-center gap-3 py-3">
                    {inner}
                  </div>
                );
              })}
            </div>

            <div className="mt-auto flex items-center gap-2 pt-7 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-ink-500">
              <span className="relative flex size-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex size-1.5 rounded-full bg-success" />
              </span>
              Currently available for new engagements
            </div>
          </motion.div>

          {/* form */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="panel rounded-[var(--radius-lg)] p-7 lg:p-8"
          >
            {sent ? (
              <div className="flex h-full flex-col items-center justify-center gap-4 py-10 text-center">
                <CheckCircle2 className="size-12 text-success" />
                <h3 className="font-display text-xl font-semibold text-ink-50">
                  {viaMailto ? "Transmission composed" : "Message transmitted"}
                </h3>
                <p className="max-w-sm text-sm text-ink-400">
                  {viaMailto ? (
                    <>
                      Your mail client should be open with the message ready. If it didn't open,
                      reach me directly at{" "}
                      <a href={`mailto:${EMAIL}`} className="text-signal-400 underline-offset-2 hover:underline">
                        {EMAIL}
                      </a>
                      .
                    </>
                  ) : (
                    <>
                      Received — I'll get back to you shortly at the address you provided. You can
                      also reach me directly at{" "}
                      <a href={`mailto:${EMAIL}`} className="text-signal-400 underline-offset-2 hover:underline">
                        {EMAIL}
                      </a>
                      .
                    </>
                  )}
                </p>
                <button
                  onClick={() => {
                    reset();
                    setSent(false);
                    setViaMailto(false);
                  }}
                  className="mt-2 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-ink-400 hover:text-ink-100"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <FieldLabel index="01">Name</FieldLabel>
                    <Input placeholder="Your name" invalid={!!errors.name} {...register("name")} />
                    {errors.name && <p className="mt-1.5 text-xs text-danger">{errors.name.message}</p>}
                  </div>
                  <div>
                    <FieldLabel index="02">Email</FieldLabel>
                    <Input
                      type="email"
                      placeholder="you@org.com"
                      invalid={!!errors.email}
                      {...register("email")}
                    />
                    {errors.email && <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>}
                  </div>
                </div>

                <div>
                  <FieldLabel index="03">Organization (optional)</FieldLabel>
                  <Input placeholder="Company / unit" {...register("org")} />
                </div>

                <div>
                  <FieldLabel index="04">Mission brief</FieldLabel>
                  <Textarea
                    placeholder="What are you building, and where does it need to run?"
                    invalid={!!errors.message}
                    {...register("message")}
                  />
                  {errors.message && <p className="mt-1.5 text-xs text-danger">{errors.message.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-md)] bg-signal-500 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-ink-950 transition-all hover:bg-signal-400 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:self-start sm:px-8"
                >
                  {sending ? (
                    <>
                      <span className="size-4 animate-spin rounded-full border-2 border-ink-950 border-t-transparent" />
                      Transmitting
                    </>
                  ) : (
                    <>
                      <Send className="size-4" />
                      Transmit
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
